package co.maytes.standupbot.dashboard.features.home

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.Eyebrow
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.billing.BillingStatusDto
import co.maytes.standupbot.shared.dto.billing.PlanDto
import co.maytes.standupbot.shared.dto.billing.SubscriptionStatusDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A

@Composable
fun PlanBanner(status: BillingStatusDto) {
    val tier = tierFor(status.plan)
    val statusBadge = statusBadgeLabel(status)
    val memberLabel = if (status.memberCount == 1) "1 member" else "${status.memberCount} members"

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = Spacing.lg)
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")
                property("background", tier.background)
                property("border", "1px solid ${tier.borderColor}")
            },
        padded = false,
    ) {
        Row(modifier = Modifier.fillMaxWidth()) {
            Box(
                modifier = Modifier.styleModifier {
                    property("width", "5px")
                    property("background", tier.accentBar)
                    property("flex-shrink", "0")
                },
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = Spacing.lg, leftRight = Spacing.lg)
                    .gap(Spacing.xl)
                    .styleModifier { property("flex-wrap", "wrap") },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Column(modifier = Modifier.styleModifier { property("min-width", "0") }) {
                    Eyebrow(text = "Workspace plan")
                    Row(
                        modifier = Modifier
                            .margin(top = Spacing.xs)
                            .gap(Spacing.sm)
                            .styleModifier { property("flex-wrap", "wrap") },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            text = tier.label,
                            modifier = Modifier
                                .fontSize(28.px)
                                .fontWeight(600)
                                .color(Palette.textPrimary)
                                .styleModifier {
                                    property("letter-spacing", "-0.02em")
                                    property("line-height", "1.1")
                                },
                        )
                        if (statusBadge != null) {
                            Badge(label = statusBadge.first, tone = statusBadge.second)
                        }
                    }
                }

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier
                        .gap(Spacing.xl)
                        .styleModifier { property("flex-wrap", "wrap") },
                ) {
                    InfoCell(label = "Members", value = memberLabel)
                    InfoCell(label = renewalLabelFor(status), value = activeUntilLabel(status))
                    UpgradeCta(status = status)
                }
            }
        }
    }
}

@Composable
private fun InfoCell(label: String, value: String) {
    Column(modifier = Modifier.gap(Spacing.xxs)) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500)
                .color(Palette.textTertiary)
                .styleModifier {
                    property("text-transform", "none")
                    property("letter-spacing", "0.06em")
                },
        )
        SpanText(
            text = value,
            modifier = Modifier
                .fontSize(Typography.body.size)
                .fontWeight(500)
                .color(Palette.textPrimary)
                .styleModifier { property("font-variant-numeric", "tabular-nums") },
        )
    }
}

@Composable
private fun UpgradeCta(status: BillingStatusDto) {
    if (status.plan != PlanDto.FREE) return
    if (!status.billingEnabled) {
        SpanText(
            text = BILLING_NOT_ENABLED_LABEL,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textTertiary),
        )
        return
    }
    A(
        href = "/app/settings/billing",
        attrs = Modifier
            .padding(topBottom = Spacing.xs, leftRight = Spacing.md)
            .color(Palette.onPrimary)
            .fontSize(Typography.bodySmall.size)
            .fontWeight(500)
            .styleModifier {
                property("background", Palette.primary.toString())
                property("border-radius", "${Radius.full}")
                property("text-decoration", "none")
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "6px")
                property("transition", "background 160ms ease, transform 160ms ease")
            }
            .toAttrs(),
    ) {
        SpanText("Upgrade →")
    }
}

private const val BILLING_NOT_ENABLED_LABEL = "Billing is not enabled"

private data class Tier(
    val label: String,
    val background: String,
    val borderColor: String,
    val accentBar: String,
)

private fun tierFor(plan: PlanDto): Tier = when (plan) {
    PlanDto.FREE -> Tier(
        label = "Free",
        background = Palette.surface.toString(),
        borderColor = Palette.border.toString(),
        accentBar = Palette.borderSubtle.toString(),
    )
    PlanDto.PRO -> Tier(
        label = "Pro",
        background = Palette.surface.toString(),
        borderColor = Palette.border.toString(),
        accentBar = Palette.accent.toString(),
    )
    PlanDto.BUSINESS -> Tier(
        label = "Business",
        background = Palette.surface.toString(),
        borderColor = Palette.border.toString(),
        accentBar = Palette.accent.toString(),
    )
}

private fun renewalLabelFor(status: BillingStatusDto): String = when {
    status.plan == PlanDto.FREE -> "Plan"
    status.cancelAtPeriodEnd -> "Cancels"
    status.status == SubscriptionStatusDto.TRIALING -> "Trial ends"
    else -> "Renews"
}

private fun activeUntilLabel(status: BillingStatusDto): String {
    if (status.plan == PlanDto.FREE) return "Free forever"
    val end = status.currentPeriodEnd ?: return "—"
    val parsed = runCatching { Instant.parse(end) }.getOrNull() ?: return end
    val ldt = parsed.toLocalDateTime(TimeZone.currentSystemDefault())
    val month = ldt.month.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
    return "$month ${ldt.dayOfMonth}, ${ldt.year}"
}

private fun statusBadgeLabel(status: BillingStatusDto): Pair<String, BadgeTone>? = when {
    status.status == SubscriptionStatusDto.TRIALING -> "Trialing" to BadgeTone.Accent
    status.cancelAtPeriodEnd -> "Cancels soon" to BadgeTone.Warning
    status.status == SubscriptionStatusDto.PAST_DUE -> "Past due" to BadgeTone.Warning
    status.status == SubscriptionStatusDto.CANCELED -> "Canceled" to BadgeTone.Warning
    else -> null
}
